/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Radio,
  Select,
  Space,
  Stack,
  Text,
} from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useEffect, useState } from 'react';
import { IconCalendarEvent } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import { EventS } from '../../../Service/EventS';
import { FormatS } from '../../../Service/FormatS';
import BookingEvent from '../../../Types/Event';
import { DataS } from '../../../Service/DataS';
import { ToastS } from '../../../Service/ToastS';
import { CreateContractRequest } from '../../../Types/DocumentT';
import BookitupBadge from '../../../Atoms/BookitupBadge';
import EmptyText from '../../../Atoms/EmptyText';
import { DocumentDto, PositionDto } from '../../../Service/DocumentT';
import { BrowserS } from '../../../Service/BrowserS';
import { getDocumentSummary } from '../../../Service/helper/documentSummary';

export const openCreateContractModal = (props: CreateContractRequest) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Vertrag erstellen
      </Text>
    ),
    children: <CreateContractModal {...props} />,
  });

interface AcceptOfferModalProps {
  bookitupDocument: DocumentDto;
  onAccept: () => void | Promise<void>;
  source?: 'offer' | 'contract';
}

export const openAcceptOfferModal = (props: AcceptOfferModalProps) =>
  openModal({
    size: 'lg',
    title: (
      <Text weight="bolder" size="xl">
        Angebot annehmen
      </Text>
    ),
    children: <AcceptOfferModal {...props} />,
  });

const CreateContractModal: FC<CreateContractRequest> = ({ eventId, ...rest }) => {
  const [availableTemplateNames, setTemplatesNames] = useState<string[]>([]);
  const [template, selectTemplate] = useState<string>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    DocumentS.getContractTemplatesNames()
      .then(setTemplatesNames)
      .finally(() => setLoading(false));
  }, []);

  const onConfirm = async () => {
    setLoading(true);
    const resJSON = await DocumentS.createContract({ eventId, ...rest, template });
    setLoading(false);
    if (resJSON) {
      navigate(`/events/${eventId}/contracts/${resJSON.id}`);
      closeAllModals();
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Radio.Group
        withAsterisk
        spacing="xs"
        offset="md"
        orientation="vertical"
        value={template}
        onChange={selectTemplate}
        label="Vorlage auswählen:"
      >
        {availableTemplateNames.length === 0 && !loading && <EmptyText />}
        {availableTemplateNames.map((name) => (
          <Radio key={name} value={name} label={name} />
        ))}
      </Radio.Group>
      <Center mt={30}>
        <Button onClick={onConfirm} disabled={loading || !template} sx={{ width: 250 }}>
          Speichern
        </Button>
      </Center>
    </>
  );
};

const AcceptOfferModal: FC<AcceptOfferModalProps> = ({ bookitupDocument, onAccept, source = 'offer' }) => {
  const { id: documentId, positions, currency, taxType, discount } = bookitupDocument;
  const [loading, setLoading] = useState(false);
  const requiredPositions = positions.filter((position) => !position.optional);
  const optionalPositions = positions.filter((position) => position.optional);
  const [selectedOptionalPositionIds, setSelectedOptionalPositionIds] = useState<number[]>(
    optionalPositions.filter((position) => position.autoSelect).map((position) => position.id),
  );

  const toggleOptionalPosition = (positionId: number) => {
    setSelectedOptionalPositionIds((current) =>
      current.includes(positionId) ? current.filter((id) => id !== positionId) : [...current, positionId],
    );
  };

  const acceptedPositions = positions.filter(
    (position) => !position.optional || selectedOptionalPositionIds.includes(position.id),
  );
  const acceptedSummary = getDocumentSummary(bookitupDocument, acceptedPositions);
  const {
    subtotal: acceptedSubtotal,
    discountAmount: acceptedDiscountAmount,
    total: acceptedTotal,
    netTotal: acceptedNetTotal,
    taxEntries: sortedAcceptedTaxEntries,
  } = acceptedSummary;
  const { value: discountValue = 0, type: discountType = 'PERCENT', description: discountDescription } = discount ?? {};
  const hasDiscount = discountValue > 0 && acceptedDiscountAmount > 0;
  const negativeSum = acceptedSubtotal - acceptedDiscountAmount < 0;

  const onConfirm = async () => {
    setLoading(true);
    const acceptedPositionIds = [...requiredPositions.map((position) => position.id), ...selectedOptionalPositionIds];
    const acceptedDocument = await DocumentS.acceptOffer(documentId, acceptedPositionIds);
    setLoading(false);
    if (acceptedDocument) {
      ToastS.success('document-accepted', 'Angebot angenommen.');
      await onAccept();
      closeAllModals();
    }
  };

  const renderPosition = (position: PositionDto) => {
    const isSelected = selectedOptionalPositionIds.includes(position.id);
    const isOptional = position.optional;

    return (
      <Stack key={position.id} spacing="md">
        <div
          role={isOptional ? 'checkbox' : undefined}
          aria-checked={isOptional ? isSelected : undefined}
          tabIndex={isOptional ? 0 : undefined}
          onClick={isOptional ? () => toggleOptionalPosition(position.id) : undefined}
          onKeyDown={
            isOptional
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleOptionalPosition(position.id);
                  }
                }
              : undefined
          }
          style={{
            padding: '5px',
            borderRadius: 10,
            cursor: isOptional ? 'pointer' : 'default',
            transition: 'background-color 120ms ease, border-color 120ms ease',
          }}
        >
          <Group position="apart" align="stretch" noWrap>
            <div style={{ flex: 1 }}>
              <Text weight="bolder" size="md">
                {BrowserS.getTextContent(position.label)}
              </Text>
              {position.subtext && (
                <div style={{ marginTop: 10 }} dangerouslySetInnerHTML={{ __html: position.subtext }} />
              )}
            </div>
            <Stack justify="space-between" align="flex-end" sx={{ minWidth: 120, alignSelf: 'stretch' }}>
              <div>
                {isOptional && (
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleOptionalPosition(position.id)}
                    onClick={(event) => event.stopPropagation()}
                    mt={2}
                  />
                )}
              </div>
              <Text size="sm" color="dimmed" align="right">
                {FormatS.formatPriceWithCurrency(currency, position.price)}
              </Text>
            </Stack>
          </Group>
        </div>
        <Divider />
      </Stack>
    );
  };

  return (
    <>
      <Text mb="md">
        {source === 'contract'
          ? 'Der Vertrag ist mit einem Angebot verknüpft. Bitte wähle aus, welche optionalen Positionen aus diesem Angebot angenommen werden sollen.'
          : 'Wähle aus, welche optionalen Positionen angenommen werden sollen.'}
      </Text>
      <Box mt="xl">
        <BookitupBadge title="Positionen" />
      </Box>
      <Stack spacing="md">
        {positions.length === 0 && <EmptyText>Keine Positionen vorhanden.</EmptyText>}
        <Space />
        {positions.map(renderPosition)}
      </Stack>
      <Box mt="xl" mb="sm">
        <BookitupBadge title="Zusammenfassung" />
      </Box>
      <Stack
        spacing="xs"
        p="md"
        sx={(theme) => ({
          borderRadius: 10,
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        })}
      >
        {hasDiscount && (
          <Group position="apart">
            <Text>Zwischensumme {taxType === 'net' ? 'netto' : ''}</Text>
            <Text>{FormatS.formatPriceWithCurrency(currency, acceptedSubtotal)}</Text>
          </Group>
        )}
        {hasDiscount && (
          <Group position="apart">
            <Group spacing="xs">
              <Text>{discountDescription ? `Rabatt (${discountDescription})` : 'Rabatt'}</Text>
              <Text size="sm">
                {discountType === 'ABSOLUTE'
                  ? FormatS.formatPriceWithCurrency(currency, discountValue)
                  : `${discountValue} %`}
              </Text>
            </Group>
            <Text>{`- ${FormatS.formatPriceWithCurrency(currency, acceptedDiscountAmount)}`}</Text>
          </Group>
        )}
        {taxType === 'net' && sortedAcceptedTaxEntries.length > 0 && (
          <Stack spacing={4} pt="xs">
            <Group position="apart">
              <Text color="dimmed">Gesamt netto</Text>
              <Text>{FormatS.formatPriceWithCurrency(currency, acceptedNetTotal)}</Text>
            </Group>
            {sortedAcceptedTaxEntries.map((taxEntry) =>
              taxEntry.taxRatePercent === 0 ? null : (
                <Group key={taxEntry.taxRatePercent} position="apart">
                  <Text size="sm" color="dimmed">{`USt. ${taxEntry.taxRatePercent}%`}</Text>
                  <Text size="sm">{FormatS.formatPriceWithCurrency(currency, taxEntry.taxAmount)}</Text>
                </Group>
              ),
            )}
          </Stack>
        )}
        {hasDiscount && <Divider />}

        <div>
          <Group position="apart">
            <Text weight="bolder">Gesamtbetrag</Text>
            <Text weight="bolder">{FormatS.formatPriceWithCurrency(currency, acceptedTotal)}</Text>
          </Group>
          {taxType === 'gross' && sortedAcceptedTaxEntries.length > 0 && (
            <>
              {sortedAcceptedTaxEntries.map((taxEntry) =>
                taxEntry.taxRatePercent === 0 ? null : (
                  <Text key={taxEntry.taxRatePercent} size="xs">
                    {`enthält USt. ${taxEntry.taxRatePercent} % (${FormatS.formatPriceWithCurrency(
                      currency,
                      taxEntry.taxAmount,
                    )})`}
                  </Text>
                ),
              )}
            </>
          )}
        </div>
      </Stack>
      <Group position="right" spacing="xs" mt={40}>
        <Button variant="default" onClick={() => closeAllModals()}>
          Abbrechen
        </Button>
        <Button
          rightIcon={loading && <Loader size="xs" />}
          disabled={loading || positions.length === 0}
          onClick={onConfirm}
        >
          Angebot annehmen
        </Button>
      </Group>
    </>
  );
};

interface ChangeEventProps {
  bookitupDocument: DocumentDto;
  onChange: () => void;
}

// eslint-disable-next-line import/prefer-default-export
export const openAssignEventModal = (props: ChangeEventProps) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Event zuordnen
      </Text>
    ),
    children: <AssignEventModal {...props} />,
  });

const AssignEventModal: FC<ChangeEventProps> = ({ bookitupDocument, onChange }) => {
  const { id: documentId, eventId: initialEventId, customerId } = bookitupDocument;
  const [loading, setLoading] = useState(false);
  const [selectedEvent, selectEvent] = useState<string | null>(null); // Let's use string for better usage with Select data
  const [bookingEvents, setBookingEvents] = useState<BookingEvent[]>([]);

  const { length } = bookingEvents;
  const emptyEvents = !loading && length === 0;
  const btnDisabled = initialEventId === Number(selectedEvent) || loading;

  useEffect(() => {
    setLoading(true);
    EventS.fetchEventsByCustomerParticipations(customerId)
      .then(setBookingEvents)
      .finally(() => setLoading(false));
  }, [customerId]);

  const onConfirm = async () => {
    if (selectedEvent) {
      setLoading(true);
      const patched = await DocumentS.patchDocument(documentId, { eventId: Number(selectedEvent) });
      setLoading(false);
      if (patched) {
        ToastS.success('foo', 'Event zugewiesen');
        onChange();
        closeAllModals();
      }
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Select
        placeholder="Event wählen"
        icon={<IconCalendarEvent />}
        data={DataS.getEventOptions(bookingEvents)}
        value={selectedEvent}
        onChange={selectEvent}
        nothingFound={
          emptyEvents && (
            <Text>Es wurde kein Event zu diesem Kontakt gefunden. Bitte füge den Kontakt erst einem Event hinzu.</Text>
          )
        }
      />
      <Center mt="xl">
        <Button onClick={onConfirm} disabled={btnDisabled} sx={{ width: 250 }}>
          Speichern
        </Button>
      </Center>
    </>
  );
};
